import chroma from 'chroma-js';
import * as twDefaultColors from 'tailwindcss/colors';
import { DefaultColors } from 'tailwindcss/types/generated/colors';

type CueTwDefaultColors = DefaultColors & {
  [key: string]: Record<string, string>;
};

export const twColors = (
  twDefaultColors ? { ...twDefaultColors } : {}
) as CueTwDefaultColors;

// TODO: Get colors from tailwind resolveConfig,
export const colors = {
  green90: '#DEF7EC',
  green100: '#D1FAE5',
  green500: '#0E9F6E',
  green600: '#057A55',
  green700: '#046C4E',
  green800: '#065F46',
  red100: '#FEE2E2',
  red500: '#F05252',
  red600: '#E02424',
  red700: '#C81E1E',
  red800: '#991B1B',
  orange100: '#ffedd5',
  orange800: '#9a3412',
  gray100: '#f3f4f6',
  gray800: '#1f2937',
};

export const reportingChartColors = [
  'cyan',
  'blue',
  'indigo',
  'purple',
  'orange',
  'yellow',
  'emerald',
].map((color) => String((twColors as any)[color]['500']));

export const isColorLight = (hex: string) => {
  return chroma(hex).luminance() >= 0.5;
};

const colorsToPickForPalette = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
];

const twColorsForPalette = colorsToPickForPalette.map((colorName) => {
  return {
    twName: colorName,
    palette: Object.entries(
      twDefaultColors[colorName as keyof typeof twDefaultColors],
    ).map(([ind, hex]) => ({
      ind,
      hex,
    })),
  };
});

export const generateTailwindColorPalette = (selectedColorHex: string) => {
  const selectedColor = chroma(selectedColorHex);
  const selectedColorH = selectedColor.get('hsl.h');
  const selectedColorS = selectedColor.get('hsl.s');
  const selectedColorL = selectedColor.get('hsl.l');

  const decisionTwColors = twColorsForPalette.map((twColor) => {
    const palette = twColor.palette.map((palette) => ({
      ...palette,
      deltaE: chroma.deltaE(selectedColorHex, palette.hex),
      lDiff: 999,
    }));

    const closest = palette.reduce((prev, current) =>
      prev.deltaE < current.deltaE ? prev : current,
    );

    return {
      ...twColor,
      palette,
      closest: closest,
      closestLDiff: closest,
    };
  });

  const closestTwColor = decisionTwColors.reduce((prev, current) =>
    prev.closest.deltaE < current.closest.deltaE ? prev : current,
  );

  closestTwColor.palette = closestTwColor.palette.map((palette) => {
    return {
      ...palette,
      lDiff: Math.abs(chroma(palette.hex).get('hsl.l') - selectedColorL),
    };
  });

  closestTwColor.closestLDiff = closestTwColor.palette.reduce(
    (prev, current) => (prev.lDiff < current.lDiff ? prev : current),
  );

  const closestLDiffH = chroma(closestTwColor.closestLDiff.hex).get('hsl.h');
  const closestLDiffS = chroma(closestTwColor.closestLDiff.hex).get('hsl.s');
  const hDiff = (selectedColorH || 0) - (closestLDiffH || 0);
  const sDiff = selectedColorS / closestLDiffS;

  let setHValue = '';
  if (hDiff === 0) {
    setHValue += closestLDiffH;
  } else if (hDiff > 0) {
    setHValue += '+' + hDiff;
  } else {
    setHValue += hDiff;
  }

  return closestTwColor.palette.reduce(
    (acc, palette) => {
      const color = chroma(palette.hex)
        .set('hsl.s', chroma(palette.hex).get('hsl.s') * sDiff)
        .set('hsl.h', setHValue);

      acc[palette.ind] =
        palette.ind === closestTwColor.closestLDiff.ind
          ? selectedColorHex
          : color.hex();

      return acc;
    },
    {} as Record<string, string>,
  );
};

export const addCustomTwColorPalette = (hex: string, removeOthers = false) => {
  if (removeOthers) {
    Object.keys(twColors).forEach((key) => {
      if (key.startsWith('#')) {
        delete twColors[key];
      }
    });
  }

  twColors[hex] = generateTailwindColorPalette(hex);
};
